<script>
import { formatMoney } from 'accounting';
import { GlLoadingIcon, GlIcon, GlTooltipDirective } from '@gitlab/ui';
import TransactionCard from '~/javascripts/purchase/components/transaction_card.vue';
import { toLocaleDateString } from '~/javascripts/lib/datetime';
import { sprintf, __ } from '~/javascripts/lib/locale';

const I18N_INFO = '%{price} per user per year';
const I18N_SUMMARY_DATES = '%{startDate} - %{endDate}';
const I18N_TOOLTIP = __(
  'Additional seats have the same term as your main subscription, and the price prorated accordingly.',
);

export default {
  name: 'AddSeatsInvoice',
  components: { TransactionCard, GlIcon, GlLoadingIcon },
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  props: {
    invoice: {
      type: Object,
      required: false,
      default: () => {},
    },
    invoiceItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: true,
    },
    showInfoMessage: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    amount() {
      return this.invoice.amount > 0 ? formatMoney(this.invoice.amount) : '–';
    },
    amountWithTax() {
      return this.invoice.amount_without_tax > 0
        ? formatMoney(this.invoice.amount_without_tax)
        : '–';
    },
    taxAmount() {
      return this.invoice.tax_amount > 0 ? formatMoney(this.invoice.tax_amount) : '–';
    },
    chargeInvoiceItems() {
      return this.invoiceItems.filter((item) => item.processing_type === 'Charge');
    },
    isDiscounted() {
      return this.invoiceItems.find((item) => item.processing_type === 'Discount');
    },
    lastItem() {
      return this.chargeInvoiceItems.slice(-1)[0];
    },
    infoMessage() {
      return sprintf(this.$options.i18n.infoMessage, {
        price: formatMoney(this.lastItem.unit_price),
      });
    },
    subscriptionRange() {
      return sprintf(this.$options.i18n.dates, {
        startDate: toLocaleDateString(this.lastItem.service_start_date),
        endDate: toLocaleDateString(this.lastItem.service_end_date),
      });
    },
  },
  i18n: {
    infoMessage: I18N_INFO,
    tooltipNote: I18N_TOOLTIP,
    dates: I18N_SUMMARY_DATES,
  },
};
</script>

<template>
  <transaction-card :title="__('Purchase summary')">
    <gl-loading-icon v-if="loading" />
    <div v-else-if="invoiceItems.length">
      <div class="gl-flex gl-justify-between gl-pb-5">
        <div>
          <strong>Price</strong>
          <div v-if="showInfoMessage" data-testid="info-message">
            <p class="gl-mt-1 gl-text-gray-300">
              {{ infoMessage }}
            </p>
            <div class="gl-flex gl-items-center">
              <p class="gl-text-gray-300">
                {{ subscriptionRange }}
              </p>
              <gl-icon
                v-gl-tooltip.right
                :title="$options.i18n.tooltipNote"
                :aria-label="$options.i18n.tooltipNote"
                class="gl-ml-2"
                name="question"
                variant="subtle"
                role="tooltip"
              />
            </div>
          </div>
        </div>
        <div class="gl-my-auto gl-ml-5">
          <span data-testid="amount">{{ amount }}</span>
        </div>
      </div>
      <div
        class="gl-flex gl-justify-between gl-gap-5 gl-border-t-1 gl-border-gray-100 gl-py-5 gl-border-t-solid"
      >
        <div>
          <strong>Subtotal</strong>
          <div
            v-if="isDiscounted"
            class="gl-mt-1 gl-text-gray-300"
            data-testid="discount-info-message"
          >
            Price includes discount and prorated charges
          </div>
        </div>
        <div class="gl-my-auto">
          <span data-testid="amount-with-tax">{{ amountWithTax }}</span>
        </div>
      </div>
      <div
        class="gl-flex gl-justify-between gl-border-t-1 gl-border-gray-100 gl-pt-5 gl-border-t-solid"
      >
        <strong>Estimated taxes</strong>
        <div class="gl-my-auto">
          <span data-testid="tax-amount">{{ taxAmount }}</span>
        </div>
      </div>
    </div>
  </transaction-card>
</template>
